'use client'

import {
  FC,
  type MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import useSuspenseSwr from '../../hooks/useSuspenseSwr'
import {
  trackButtonClick,
  trackExternalLink,
  trackModalClose,
  trackSceneJump,
  trackPageLeave,
  trackSessionStart,
  trackVideoEnd,
  trackVideoStart,
} from '../../lib/analytics'
import { isInlineDisplayType } from '../../lib/displayType'
import { usePageHide, usePlayedSecondsCounter } from '../../lib/playedSeconds'
import SwrKey from '../../lib/swrKey'
import { getPublicScenario } from '../../orval/generated/public/scenarios/scenarios'
import { getPublicTenant } from '../../orval/generated/public/tenants/tenants'
import { ButtonActionType, Hotspot, InteractiveButton, PlayerConfig } from '../../types/interactiveVideo'
import { TenantButtonColorProvider } from '../TenantButtonColorContext'
import { TenantThemeModeProvider } from '../TenantThemeModeContext'
import { convertScenarioToPlayerConfigSync } from '../scenario/utils/scenarioToPlayerConfig'
import { videoAccentStyle, videoFrameStyle } from '../video/videoAccentTheme'
import { useBridge } from './bridge/BridgeContext'
import EmbeddingAiInterviewPresentation from './embedding-ai-interview-presentation'
import { ShadowHostPortalProvider, useShadowHostPortal } from './shadow-host-portal/ShadowHostPortalContext'

export type EmbeddingAiInterviewProps = {
  scenarioId: string
  /**
   * スクリプトタグの `data-tenant-id` 属性から渡される tenantId。
   * 指定されていれば scenarios / tenants を並列で取得し、1段分の RTT を削減する。
   * 未指定の場合は scenario レスポンスの tenantId で直列取得する（後方互換）。
   */
  tenantId?: string
  shadowHost?: HTMLDivElement
  container?: HTMLDivElement
  /**
   * 強制全画面オーバーレイモード。
   * scenario.displayType が SLIDER / TILE であっても、shadowHost を
   * `position: fixed; inset: 0;` の全画面オーバーレイとして配置し、
   * 描画も PopupPlayer に切り替える。
   *
   * parent-bridge 経由で親ページ直下に mount された場合に true を渡す。
   * これにより iframe（STUDIO 等）の縦横比制約から解放され、
   * 親ページ全体（100vw × 100vh）に動画モーダルを展開できる。
   *
   * 直接埋め込みの場合は false（既定）のまま使う。
   */
  forceOverlay?: boolean
  /**
   * 起動時に自動で再生する動画の videoId。
   * 指定されていれば、その videoId に一致する node を currentNode として
   * `isPlayerOpen=true` の状態でモーダルを起動する（auto-open）。
   *
   * parent-bridge 経由でユーザーがクリックしたタイルの動画 ID を受け渡す
   * 用途を想定している。指定が無ければ scenario.initialNodeKey の動画から開始する。
   */
  initialVideoId?: string
  /**
   * モーダルを閉じた際に呼ばれるコールバック。
   * parent-bridge から受け渡し、親ページに作った container 要素を撤去する用途。
   */
  onTeardown?: () => void
  /**
   * 案E: hidden pre-mount モード
   * true の場合、forceOverlay 経由の auto-open useEffect を抑止し、
   * 外部から `openHandle.open()` が呼ばれるまでモーダルを閉じたまま待機する。
   *
   * 親側 parent-bridge が PREPARE 受信時にこのフラグを立てて事前 mount し、
   * OPEN 受信時に openHandle で初めてモーダルを可視化することで、
   * クリック→可視化の経路から重い React mount を排除できる。
   */
  noAutoOpen?: boolean
  /**
   * 案E: hidden pre-mount モードでの「外部からモーダルを開く」命令を受け取る ref。
   *
   * `openHandle.current.open(videoId?)` が呼ばれると:
   * - videoId に対応する node を currentNode に設定
   * - isPlayerOpen=true にしてモーダルを可視化
   *
   * forwardRef を使わずにオプショナル prop として渡すことで、既存の
   * EmbeddingAiInterview の利用箇所（direct / inline-fallback）には一切影響しない。
   */
  openHandle?: MutableRefObject<EmbeddingOpenApi | null>
  /**
   * Scriptタグの `data-mode` 属性。
   * `'single'` のとき、`modeVideoId` で指定された動画のサムネイルを単独表示する。
   */
  mode?: string
  /**
   * mode='single' で単独表示する動画 ID（Scriptタグの `data-video-id` 属性）。
   */
  modeVideoId?: string
  /**
   * scenario.displayType が確定した時点で 1 度だけ呼ばれるコールバック。
   *
   * embedded.js（Light DOM 側）が「Powered by moovy」クレジットの配置を
   * 表示形態に合わせて切り替えるために使う。POPUP 型はウィジェット本体が
   * `position: fixed` で画面隅に切り離されるため、Light DOM のスクリプト位置に
   * 置いたままだとクレジットだけが取り残される（ヘッダーが無いサイトで露出）。
   * displayType を受け取って fixed 配置に補正する用途。
   */
  onDisplayResolved?: (displayType: string | undefined) => void
  /**
   * iframe 埋め込みページ（`/embed/{scenarioId}`）で動作しているか。
   * embedded.js がスクリプトタグの `data-frame-embed` 属性から渡す。
   * true のとき、モーダルの間だけ iframe を全画面化して script 埋め込みと同じ
   * 「ページ全体を覆うモーダル」を再現する（ShadowHostPortalContext）。
   */
  frameEmbed?: boolean
}

/** 同じ動画の「最後まで再生」がこの時間内に重ねて届いたら、2回目以降は送らない */
const VIDEO_END_DEDUPE_MS = 2000

/** 案E: 親側 parent-bridge から呼べる imperative open API */
export type EmbeddingOpenApi = {
  open: (videoId?: string) => void
}

type Scenario = NonNullable<ReturnType<typeof useScenario>['data']>

const useScenario = (scenarioId: string) =>
  useSuspenseSwr([SwrKey.PUBLIC_SCENARIO, scenarioId], () => getPublicScenario(scenarioId), {
    fallbackData: null,
  })

/**
 * displayType に応じて Shadow DOM ホストと内部コンテナのスタイルを適用する。
 * React の render パスから切り離された純粋な DOM 操作として抽出し、
 * ライフサイクルと責務を明確化する。
 */
const applyDisplayStyles = (
  shadowHost: HTMLDivElement | undefined,
  container: HTMLDivElement | undefined,
  displayType: string | undefined,
  forceOverlay = false,
) => {
  if (!shadowHost || !container) return
  // forceOverlay=true のときは scenario.displayType に関係なく popup スタイルに固定する。
  // parent-bridge 経由でマウントされたケース等、iframe の縦横比から脱出して
  // 親ページ全体に全画面オーバーレイを出したい用途で使う。
  // SLIDER/TILE は一覧、BOX は入口バナー、WIDE はプレイヤー本体を
  // その場に描画するため、いずれも shadowHost を固定配置してはいけない。
  const isInline = !forceOverlay && isInlineDisplayType(displayType)
  if (isInline) {
    shadowHost.style.position = 'static'
    shadowHost.style.inset = 'auto'
    shadowHost.style.display = 'block'
    shadowHost.style.width = '100%'
    shadowHost.style.pointerEvents = 'auto'
    // モーダル展開分岐（else）で設定された z-index を明示的にクリアする。
    // 値を持ったまま position: static になると見た目には効かないが、
    // 後続で position が変わった時に意図しない順序で前面化する原因になる。
    shadowHost.style.zIndex = ''

    container.style.position = 'static'
    container.style.inset = 'auto'
    container.style.display = 'block'
    container.style.width = '100%'
    container.style.height = 'auto'
    container.style.pointerEvents = 'auto'
  } else {
    shadowHost.style.position = 'fixed'
    shadowHost.style.inset = '0'
    shadowHost.style.pointerEvents = 'none'
    // 32bit 整数の最大値。先方サイトの sticky ヘッダー等が設定し得る
    // 現実的な z-index を確実に上回るための値。
    shadowHost.style.zIndex = '2147483647'

    container.style.position = 'fixed'
    container.style.inset = '0'
    container.style.pointerEvents = 'none'
  }
}

/**
 * LCP 候補のサムネイル画像を <link rel="preload" fetchpriority="high"> で先読みする。
 * scenario レスポンスから initialNodeKey のサムネイル URL を取り出し、
 * React が <img> を描画する前にブラウザのネットワーク発火を促す。
 * 同一 URL の重複注入を防ぐため、ページ内で 1 回だけ出力する。
 */
const preloadInitialThumbnail = (thumbnailUrl: string | undefined | null) => {
  if (!thumbnailUrl || typeof document === 'undefined') return
  const selector = `link[rel="preload"][href="${CSS.escape(thumbnailUrl)}"]`
  if (document.head.querySelector(selector)) return
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = thumbnailUrl
  // fetchpriority は as="image" preload に対して高優先度を指示する
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
}

/**
 * 動画配信ホスト（CDN）へ事前接続（preconnect）する。
 * クリック前に DNS/TCP/TLS の握手を済ませておき、初回の動画取得を速める。
 * データは取得しないため通信費は増えない。同一オリジンは 1 回だけ出力する。
 */
const preconnectVideoHost = (videoUrl: string | undefined | null) => {
  if (!videoUrl || typeof document === 'undefined') return
  let origin: string
  try {
    origin = new URL(videoUrl).origin
  } catch {
    return
  }
  const selector = `link[rel="preconnect"][href="${CSS.escape(origin)}"]`
  if (document.head.querySelector(selector)) return
  const link = document.createElement('link')
  link.rel = 'preconnect'
  link.href = origin
  document.head.appendChild(link)
}

const EmbeddingAiInterview: FC<EmbeddingAiInterviewProps> = ({
  scenarioId,
  tenantId,
  shadowHost,
  container,
  forceOverlay = false,
  initialVideoId,
  onTeardown,
  noAutoOpen = false,
  openHandle,
  mode,
  modeVideoId,
  onDisplayResolved,
  frameEmbed = false,
}) => {
  // 公開シナリオデータを取得
  const { data: scenario, error } = useScenario(scenarioId)
  // テナントデータを取得（ロゴ・テーマ等の共有データ用）
  // props の tenantId（スクリプトタグの data-tenant-id）が渡されていれば、
  // scenario の取得完了を待たず並列に取得する（RTTを 2 → 1 に短縮）。
  // 未指定時は scenario.tenantId を解決してから取得する（後方互換フォールバック）。
  const effectiveTenantId = tenantId ?? scenario?.tenantId ?? null
  const { data: tenant } = useSuspenseSwr(
    effectiveTenantId ? [SwrKey.PUBLIC_TENANT, effectiveTenantId] : null,
    () => (effectiveTenantId ? getPublicTenant(effectiveTenantId) : Promise.resolve(null)),
    { fallbackData: null },
  )

  const [playerConfig, setPlayerConfig] = useState<PlayerConfig | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  // アナリティクス用の状態管理
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)
  const [currentNodeKey, setCurrentNodeKey] = useState<string | null>(null)
  const videoStartTimeRef = useRef<number>(0)
  const hasTrackedSessionRef = useRef(false)
  // 実際に再生した秒数を数える。対象はプレイヤー内の VideoCanvas の <video>（data-moovy-main-video）
  const playerRootRef = useRef<HTMLDivElement>(null)
  const playedCounter = usePlayedSecondsCounter(
    () => playerRootRef.current?.querySelector<HTMLVideoElement>('video[data-moovy-main-video]') ?? null,
    isPlayerOpen,
  )
  // forceOverlay モード時の auto-open を 1 回だけ発火させるためのガード
  const hasAutoOpenedRef = useRef(false)
  // 案E: openHandle.open(videoId) で動的に上書きする videoId。
  // 通常パス（direct / OPEN-on-click 経由）では undefined のままで、
  // prop の initialVideoId がそのまま Presentation に渡される。
  // pre-mount + openHandle.open(videoId) のケースだけここに値が入り、
  // initialVideoId prop を上書きして Presentation の VideoModal の
  // initialVideoIndex を切り替える。
  const [overriddenVideoId, setOverriddenVideoId] = useState<string | undefined>(undefined)
  const effectiveVideoId = overriddenVideoId ?? initialVideoId

  // シナリオ確定時に Shadow DOM のスタイルを一度だけ適用する（FOUC最小化）
  useEffect(() => {
    applyDisplayStyles(shadowHost, container, scenario?.displayType, forceOverlay)
  }, [shadowHost, container, scenario?.displayType, forceOverlay])

  // displayType が確定したら Light DOM 側（embedded.js）へ通知する。
  // クレジット配置を表示形態に合わせて補正するために使う。
  useEffect(() => {
    if (!scenario) return
    onDisplayResolved?.(scenario.displayType)
  }, [scenario, onDisplayResolved])

  // scenario 到着直後（React描画より早い段階で）、入口サムネイルを優先して preload する。
  // 未設定の場合だけ初期ノードの動画サムネイルへフォールバックする。
  // LCP の "Load delay" (旧 576ms) を最短化するための最重要改善。
  useEffect(() => {
    if (!scenario) return
    const initialNode = scenario.nodes?.find((n) => n.nodeKey === scenario.initialNodeKey)
    preloadInitialThumbnail(scenario.popupThumbnailUrl ?? initialNode?.thumbnailUrl)
    // 動画CDNへ事前接続して初回再生の接続待ちを短縮（データは取得しないので通信費増なし）
    preconnectVideoHost(initialNode?.videoUrl)
  }, [scenario])

  // 「今の動画」を最初の動画に合わせたシナリオ。別タブから戻ったときの再取得では合わせ直さない
  const initializedScenarioIdRef = useRef<string | null>(null)

  // シナリオデータをプレイヤー設定に変換
  useEffect(() => {
    if (!scenario) return
    try {
      setLoadError(null)
      // 別タブから戻るとシナリオが再取得され（署名付きURLが変わるため別オブジェクトになる）、ここが再実行される。
      // そのたびに最初の動画へ戻すと、視聴中の動画のボタン・閉じる記録が最初の動画名で送られてしまう。
      if (initializedScenarioIdRef.current !== scenario.id) {
        initializedScenarioIdRef.current = scenario.id
        setCurrentNodeKey(scenario.initialNodeKey)
      }

      // シナリオノードから動画URLのマップを作成（APIレスポンスには署名付きURLが含まれている）
      const videoUrlMap = new Map<string, string>()
      const hlsUrlMap = new Map<string, string>()
      for (const node of scenario.nodes || []) {
        if (node.videoId && node.videoUrl) {
          videoUrlMap.set(node.videoId, node.videoUrl)
        }
        // HLSが利用可能(READY)なノードのみ。未対応/未変換は videoUrl(MP4) にフォールバック
        if (node.videoId && node.hlsUrl) {
          hlsUrlMap.set(node.videoId, node.hlsUrl)
        }
      }

      setPlayerConfig(convertScenarioToPlayerConfigSync(scenario as Scenario, videoUrlMap, hlsUrlMap))
    } catch (err) {
      console.error('プレイヤー設定の読み込みエラー:', err)
      setLoadError('動画の読み込みに失敗しました')
    }
  }, [scenario])

  // セッション開始時のトラッキング（StrictModeでの二重実行を防止）
  useEffect(() => {
    if (!isPlayerOpen || !scenario || hasTrackedSessionRef.current) return
    hasTrackedSessionRef.current = true
    trackSessionStart(scenarioId)
    const initialNode = scenario.nodes.find((n) => n.nodeKey === scenario.initialNodeKey)
    if (initialNode) {
      trackVideoStart(scenarioId, scenario.initialNodeKey, initialNode.video?.duration || undefined)
    }
    videoStartTimeRef.current = Date.now()
    playedCounter.reset()
  }, [isPlayerOpen, scenarioId, scenario, playedCounter])

  // forceOverlay モード（parent-bridge 経由）の auto-open。
  // - scenario が確定したら一度だけ発火し、isPlayerOpen=true にする
  // - initialVideoId が指定されていれば対応する node を currentNode に設定
  // - 該当 node が無ければ scenario.initialNodeKey で開く（既定の挙動）
  // - 案E（noAutoOpen=true、hidden pre-mount）の場合は openHandle.open() が
  //   呼ばれるまで auto-open しない
  useEffect(() => {
    if (!forceOverlay || !scenario) return
    if (noAutoOpen) return
    if (hasAutoOpenedRef.current) return
    hasAutoOpenedRef.current = true
    if (initialVideoId) {
      const targetNode = scenario.nodes.find((n) => n.videoId === initialVideoId)
      if (targetNode) {
        setCurrentNodeKey(targetNode.nodeKey)
      }
    }
    setIsPlayerOpen(true)
  }, [forceOverlay, scenario, initialVideoId, noAutoOpen])

  // 案E: 外部（parent-bridge）から「モーダルを開いて」と命令を受け取るための imperative API。
  // useImperativeHandle は ref として渡された MutableRefObject の .current に
  // ハンドラオブジェクトを設定する。openHandle 未指定時（direct/inline-fallback）は
  // .current が常に null のままで何の副作用も無い。
  useImperativeHandle(
    openHandle,
    () => ({
      open: (videoId?: string) => {
        // Presentation に渡る initialVideoId を切り替える
        if (videoId !== undefined) {
          setOverriddenVideoId(videoId)
        }
        if (videoId && scenario) {
          const targetNode = scenario.nodes.find((n) => n.videoId === videoId)
          if (targetNode) {
            setCurrentNodeKey(targetNode.nodeKey)
          }
        }
        // 既存の auto-open useEffect の二重発火を抑止
        hasAutoOpenedRef.current = true
        setIsPlayerOpen(true)
      },
    }),
    [scenario],
  )

  // nodeKeyからnodeを取得するヘルパー
  const getNodeByKey = useCallback(
    (nodeKey: string) => scenario?.nodes.find((n) => n.nodeKey === nodeKey),
    [scenario?.nodes],
  )

  // 動画終了時のハンドラ
  // 直前に「最後まで再生」を送った動画と時刻。プレイヤー内の2か所（InteractiveVideoPlayer と
  // VideoCanvas の ended）が同じ終了をそれぞれ知らせてくるため、同じ終了を二重に送らない。
  const lastVideoEndRef = useRef<{ nodeId: string; at: number } | null>(null)
  const handleVideoEnd = useCallback(
    (nodeId: string) => {
      const node = playerConfig?.nodes.get(nodeId)
      if (!node) return
      const now = Date.now()
      const last = lastVideoEndRef.current
      if (last && last.nodeId === nodeId && now - last.at < VIDEO_END_DEDUPE_MS) return
      lastVideoEndRef.current = { nodeId, at: now }
      const viewTime = (now - videoStartTimeRef.current) / 1000
      trackVideoEnd(scenarioId, node.nodeKey, viewTime, playedCounter.take())
    },
    [scenarioId, playerConfig, playedCounter],
  )

  // 動画切り替え時のハンドラ
  const handleVideoChange = useCallback(
    (nodeId: string) => {
      const node = playerConfig?.nodes.get(nodeId)
      if (!node) return
      setCurrentNodeKey(node.nodeKey)
      const scenarioNode = getNodeByKey(node.nodeKey)
      trackVideoStart(scenarioId, node.nodeKey, scenarioNode?.video?.duration || undefined)
      videoStartTimeRef.current = Date.now()
      playedCounter.reset()
    },
    [scenarioId, playerConfig, getNodeByKey, playedCounter],
  )

  // ボタンクリック時のハンドラ
  const handleButtonClick = useCallback(
    async (button: InteractiveButton) => {
      if (!currentNodeKey) return
      if (button.type === ButtonActionType.VIDEO_TRANSITION) {
        const action = button.action as { type: 'video_transition'; targetNodeId: string }
        const targetNode = playerConfig?.nodes.get(action.targetNodeId)
        trackButtonClick(
          scenarioId,
          currentNodeKey,
          button.id,
          button.label,
          targetNode?.nodeKey,
          playedCounter.take(),
        )
      } else if (button.type === ButtonActionType.EXTERNAL_LINK) {
        const action = button.action as { type: 'external_link'; url: string; openInNewTab: boolean }
        // トラッキングの完了を待ってからページ遷移
        await trackExternalLink(scenarioId, currentNodeKey, action.url, button.label, playedCounter.take())
      }
    },
    [scenarioId, currentNodeKey, playerConfig, playedCounter],
  )

  // 動画内のクリック領域（ADR-0022）。遷移と外部リンクは通常のボタンと同じイベントで送り、
  // ボタン別クリック数レポートに同じ宛先として積み上げる。シーン移動だけ専用のイベントにする。
  const handleHotspotClick = useCallback(
    async (hotspot: Hotspot) => {
      if (!currentNodeKey) return
      const { action } = hotspot
      if (action.type === 'video_transition') {
        const targetNode = playerConfig?.nodes.get(action.targetNodeId)
        trackButtonClick(scenarioId, currentNodeKey, hotspot.id, hotspot.label, targetNode?.nodeKey)
      } else if (action.type === 'external_link') {
        // トラッキングの完了を待ってからページ遷移
        await trackExternalLink(scenarioId, currentNodeKey, action.url, hotspot.label)
      } else {
        trackSceneJump(scenarioId, currentNodeKey, action.time, hotspot.label)
      }
    },
    [scenarioId, currentNodeKey, playerConfig],
  )

  const bridge = useBridge()

  // 案C: SWR cache 共有
  // scenario / tenant が解決した時点で BridgeProvider にデータを登録する。
  // bridge.sendOpenIntent() が postMessage する際、登録済みデータを payload に同梱して
  // 親側で SWR cache を pre-populate できるようにする（親で再 fetch を省く）。
  // 直接埋め込み（bridge=null）や setSharedData 未対応な旧 Provider では no-op。
  useEffect(() => {
    if (!bridge?.setSharedData) return
    if (!scenario) return
    bridge.setSharedData({ scenario, tenant: tenant ?? null })
  }, [bridge, scenario, tenant])

  const handleThumbnailClick = useCallback(() => {
    if (bridge) {
      bridge.sendOpenIntent()
      return
    }
    setIsPlayerOpen(true)
  }, [bridge])

  // プレイヤーを閉じる時のハンドラ
  const handleClosePlayer = useCallback(() => {
    if (currentNodeKey) {
      const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
      trackModalClose(scenarioId, currentNodeKey, viewTime, playedCounter.take())
    }
    setIsPlayerOpen(false)
    hasTrackedSessionRef.current = false
    setCurrentNodeKey(scenario?.initialNodeKey ?? null)
    // parent-bridge 経由で mount された場合、container ごと撤去する後始末を呼ぶ
    if (onTeardown) onTeardown()
  }, [scenarioId, currentNodeKey, scenario, onTeardown, playedCounter])

  // プレイヤーを開いたままブラウザのタブごと閉じられたときも、見た秒数を送る
  usePageHide(() => {
    if (!currentNodeKey) return
    const viewTime = (Date.now() - videoStartTimeRef.current) / 1000
    trackPageLeave(scenarioId, currentNodeKey, viewTime, playedCounter.take())
  }, isPlayerOpen)

  // テナントのデフォルトCTAボタン色を config に相乗りさせる。
  // nodes 等は作り直さず spread で1フィールドだけ足すため、tenant が
  // scenario より遅れて到着しても nodes Map の参照は不変（プレイヤーの再mount回避）。
  const interactiveConfig = useMemo<PlayerConfig | null>(
    () => (playerConfig ? { ...playerConfig, defaultButtonColor: tenant?.defaultButtonColor ?? null } : null),
    [playerConfig, tenant?.defaultButtonColor],
  )

  // エラー状態 / ローディング状態 / scenario 未到達のいずれも何も表示しない
  if (error || loadError) return null
  if (!interactiveConfig) return null
  if (!scenario) return null

  const presentation = (
    // CTAボタン（採用情報へ 等）がテナントデフォルト色を参照できるよう Context を張る
    <TenantThemeModeProvider value={tenant?.themeMode === 'DARK'}>
      <TenantButtonColorProvider value={tenant?.defaultButtonColor ?? null}>
        <div
          ref={playerRootRef}
          className={tenant?.themeMode === 'DARK' ? 'dark' : ''}
          data-tenant-theme={tenant?.themeMode ?? 'LIGHT'}
          style={{
            display: 'contents',
            ...videoAccentStyle(tenant?.themeMode === 'DARK'),
            ...videoFrameStyle(tenant?.defaultButtonColor),
          }}
        >
          <EmbeddingAiInterviewPresentation
            scenarioId={scenarioId}
            scenario={scenario}
            interactiveConfig={interactiveConfig}
            isPlayerOpen={isPlayerOpen}
            forceOverlay={forceOverlay}
            initialVideoId={effectiveVideoId}
            mode={mode}
            modeVideoId={modeVideoId}
            onThumbnailClick={handleThumbnailClick}
            onClosePlayer={handleClosePlayer}
            onVideoEnd={handleVideoEnd}
            onVideoChange={handleVideoChange}
            onButtonClick={handleButtonClick}
            onHotspotClick={handleHotspotClick}
          />
        </div>
      </TenantButtonColorProvider>
    </TenantThemeModeProvider>
  )

  // forceOverlay モード（parent-bridge 経由）の場合は shadowHost が既に
  // 親ページ body 直下のオーバーレイなので portal は不要。
  // Provider をスキップして PopupPlayer を直接描画する。
  if (forceOverlay) {
    return presentation
  }

  return (
    <ShadowHostPortalProvider shadowHost={shadowHost} isFrameEmbed={frameEmbed}>
      {/* shadowHost 全体を body 直下へ退避するのは固定表示の POPUP だけ。
          BOX は自前のモーダルで制御し、WIDE はクリック後も同じインライン枠を維持する。 */}
      {scenario?.displayType === 'POPUP' && <PopupPortalEffect isPlayerOpen={isPlayerOpen} />}
      {presentation}
    </ShadowHostPortalProvider>
  )
}

/**
 * POPUP 表示時、`isPlayerOpen` の変化を shadowHost portal の有効・無効に同期させる。
 * SLIDER / TILE 型は内部で独自にモーダル状態を持つため、各プレイヤーが
 * `useShadowHostPortal` を直接呼んで portal を制御する。
 *
 * SLIDER / TILE 型では `isPlayerOpen` は常に false のままなので、本コンポーネントは
 * setPortalActive(false) のみを送り続け、portal 状態を変えない（idempotent）。
 */
const PopupPortalEffect: FC<{ isPlayerOpen: boolean }> = ({ isPlayerOpen }) => {
  const portal = useShadowHostPortal()
  useEffect(() => {
    portal?.setPortalActive(isPlayerOpen)
  }, [isPlayerOpen, portal])
  return null
}

export default EmbeddingAiInterview
